import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import useSWR from 'swr';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { OutpostStatus } from '../../../../../../typings/OutpostDeployment.interface';
import LINKS from '../../../configs/links';
import { capitalizeFirstLetter } from '../../../utils';
import { showChat } from '../../../utils/liveChat';
import Avatar from '../../common/base/Avatar';
import Badge from '../../common/base/Badge';
import Button, { ClickableArea } from '../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../common/base/Card';
import Divider from '../../common/base/Divider';
import Icon from '../../common/base/Icon';
import ProgressBar from '../../common/base/ProgressBar';
import Text from '../../common/base/Text';
import Tooltip from '../../common/base/Tooltip';
import Dropdown from '../../common/Dropdown';
import { Div } from '../../common/helpers/StyledUtils';
import { GlobalContext } from '../../contexts/GlobalContext';
import { DashboardContext } from '../DashboardScene/DashboardContext';
import { StyledNavLinks } from '../DashboardScene/Nav';
import useChangelog from '../DashboardScene/Nav/Changelog/useChangelog';
import { handleLogout } from '../DashboardScene/Nav/NavFooter';
import { OutpostContext } from './OutpostContext';
const StyledNavFooter = styled(Div)<{
  collapsed: boolean;
}>(({
  theme,
  collapsed
}) => css`
    padding: ${theme.spacing(3)} ${theme.spacing(2)};

    border-top: 1px solid ${theme.colors.outline.neutral};
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${theme.colors.surface.base.background};
    z-index: ${theme.zindex.fixed};

    ${collapsed && css`
      ${StyledNavLinks} > a,
      ${StyledNavLinks} > button {
        box-sizing: border-box;
        width: ${theme.pxToRem(28)} !important;
      }
    `}
  `);
const status_map: Record<OutpostStatus, {
  theme_color: string;
  text?: string;
}> = {
  HEALTHY: {
    theme_color: 'success',
    text: 'Healthy'
  },
  UPDATING: {
    theme_color: 'primary',
    text: 'Updating...'
  },
  UNAVAILABLE: {
    theme_color: 'danger',
    text: 'Unavailable'
  },
  INITIALIZING: {
    theme_color: 'primary',
    text: 'Initializing...'
  }
} as const;
const pulseAnimation = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 0.85;
      transform: translate(-50%, -50%) scale(1.18);
    }
  }
`;
const StyledStatusIndicator = styled.div<{
  status: OutpostStatus;
}>(({
  theme,
  status
}) => css`
    position: relative;
    background-color: ${theme.colors.surface.container[status_map[status].theme_color]};
    margin: ${theme.spacing(0.5)};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    &::after {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      content: '';
      position: absolute;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: ${theme.colors.on.hue_container[status_map[status].theme_color]};
      ${status === 'UPDATING' && css`
        animation: pulse 1.5s ease-in-out infinite;
      `}
    }
    ${status === 'UPDATING' && pulseAnimation}
  `);
const CardsWrapper = styled(Div)`
  position: relative;
  margin: ${({
  theme
}) => theme.spacing(1)};
  margin-bottom: ${({
  theme
}) => theme.spacing(2)};

  ${StyledCard} {
    border: none;
    box-shadow: ${({
  theme
}) => theme.shadows.neutral.base};
  }
`;
const CardBackgroundHint = styled(StyledCard)`
  position: absolute;
  top: -4px;
  padding: ${({
  theme
}) => theme.spacing(4)};
  opacity: 0.8;
  transform: scaleX(0.9);
  z-index: -1;
`;
const StyledOutpostUsageCard = styled(StyledCard)`
  position: relative;
  width: auto;
  z-index: 1;
`;
const isOutpostStarterPlan = (outpost_plan?: string | null) => {
  return outpost_plan ? outpost_plan.toLowerCase().includes('starter') : false;
};
const useOutpostUsage = () => {
  const $ = _c(28);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  let t0;
  if ($[0] !== subscription.outpost_plan) {
    t0 = isOutpostStarterPlan(subscription.outpost_plan);
    $[0] = subscription.outpost_plan;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const is_outpost_starter_plan = t0;
  let t1;
  if ($[2] !== is_outpost_starter_plan) {
    t1 = is_outpost_starter_plan && APIMethodKeys.billing.getCard();
    $[2] = is_outpost_starter_plan;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== HookdeckAPI.billing) {
    t2 = () => HookdeckAPI.billing.getCard();
    $[4] = HookdeckAPI.billing;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: card_data
  } = useSWR(t1, t2);
  const has_payment_method = !!card_data;
  const should_fetch_usage = is_outpost_starter_plan && !has_payment_method;
  let t3;
  if ($[6] !== should_fetch_usage) {
    t3 = should_fetch_usage && APIMethodKeys.billing.getOutpostSubscriptionDetails();
    $[6] = should_fetch_usage;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== HookdeckAPI.billing) {
    t4 = () => HookdeckAPI.billing.getOutpostSubscriptionDetails();
    $[8] = HookdeckAPI.billing;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: subscription_details
  } = useSWR(t3, t4);
  let t5;
  if ($[10] !== should_fetch_usage || $[11] !== subscription_details?.current_billing_period_end_date || $[12] !== subscription_details?.current_billing_period_start_date) {
    t5 = should_fetch_usage && APIMethodKeys.billing.getUsage({
      timeframe_start: subscription_details?.current_billing_period_start_date,
      timeframe_end: subscription_details?.current_billing_period_end_date,
      product: "outpost"
    });
    $[10] = should_fetch_usage;
    $[11] = subscription_details?.current_billing_period_end_date;
    $[12] = subscription_details?.current_billing_period_start_date;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== HookdeckAPI.billing || $[15] !== subscription_details?.current_billing_period_end_date || $[16] !== subscription_details?.current_billing_period_start_date) {
    t6 = () => HookdeckAPI.billing.getUsage({
      timeframe_start: subscription_details?.current_billing_period_start_date,
      timeframe_end: subscription_details?.current_billing_period_end_date,
      product: "outpost"
    });
    $[14] = HookdeckAPI.billing;
    $[15] = subscription_details?.current_billing_period_end_date;
    $[16] = subscription_details?.current_billing_period_start_date;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const {
    data: usage_data
  } = useSWR(t5, t6);
  let t7;
  if ($[18] !== usage_data?.data) {
    t7 = usage_data?.data.find(_temp);
    $[18] = usage_data?.data;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const usage_entry = t7;
  const used_events = usage_entry?.usage[0]?.quantity || 0;
  let t8;
  if ($[20] !== subscription_details?.plan?.prices) {
    t8 = Number(subscription_details?.plan?.prices.find(_temp2)?.package_with_allocation_config?.allocation) || 50000;
    $[20] = subscription_details?.plan?.prices;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const included_events = t8;
  const is_high_usage = used_events > included_events * 0.7;
  const t9 = should_fetch_usage ? !subscription_details || !usage_data : false;
  let t10;
  if ($[22] !== included_events || $[23] !== is_high_usage || $[24] !== should_fetch_usage || $[25] !== t9 || $[26] !== used_events) {
    t10 = {
      loading: t9,
      is_usage_rendering: should_fetch_usage,
      used_events,
      included_events,
      is_high_usage
    };
    $[22] = included_events;
    $[23] = is_high_usage;
    $[24] = should_fetch_usage;
    $[25] = t9;
    $[26] = used_events;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
const OutpostUsageCard = () => {
  const $ = _c(19);
  const {
    used_events,
    included_events
  } = useOutpostUsage();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      align: "center",
      justify: "flex-start"
    };
    t1 = <Text semi={true}>Included events</Text>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      label: "Billing",
      to: "/settings/organization/billing",
      icon: "wallet"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Div flex={t0}>{t1}<Tooltip tooltip="The Outpost Starter plan includes a monthly allocation of billable events. Billable events are any events that are sent to a destination and exclude Hookdeck destination Add a payment method to continue usage as you scale." cta={t2}><Icon icon="info" muted={true} m={{
          l: 1
        }} /></Tooltip></Div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      y: 2
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = used_events / included_events;
  const t6 = used_events >= included_events;
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = <ProgressBar m={t4} value={t5} danger={t6} />;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = used_events < included_events ? used_events : included_events;
  let t9;
  if ($[8] !== t8) {
    t9 = <NumberFormat renderText={_temp3} displayType="text" value={t8} thousandSeparator="," decimalScale={0} />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== included_events) {
    t10 = <NumberFormat renderText={_temp4} displayType="text" value={included_events} thousandSeparator="," />;
    $[10] = included_events;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t9) {
    t11 = <Text muted={true}>{t9}/{t10}{" "}used</Text>;
    $[12] = t10;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Button to={_temp5} block={true} outline={true} data-action="add-payment-method-usage-navbar-card" m={{
      t: 3
    }}>Add Payment Method</Button>;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t11 || $[17] !== t7) {
    t13 = <CardsWrapper><StyledOutpostUsageCard><StyledCardSection p={3}>{t3}{t7}{t11}{t12}</StyledCardSection></StyledOutpostUsageCard></CardsWrapper>;
    $[16] = t11;
    $[17] = t7;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  return t13;
};
const OutpostNavFooter = t0 => {
  const $ = _c(52);
  const {
    show_get_started,
    setShowGetStarted
  } = t0;
  const {
    user,
    nav_collapsed,
    setNavCollapsed
  } = useContext(DashboardContext);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    status
  } = useContext(OutpostContext);
  const theme = useTheme();
  const history = useHistory();
  const {
    is_usage_rendering,
    is_high_usage,
    loading: is_usage_loading
  } = useOutpostUsage();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      product: "outpost"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    showFeed,
    unread_count,
    ChangelogCard,
    ChangelogFeed
  } = useChangelog(t1);
  const has_changelog_card = !!ChangelogCard;
  const has_usage_card = is_usage_rendering;
  const show_usage_card_first = has_usage_card && is_high_usage;
  const has_cards = is_usage_loading ? false : has_changelog_card || has_usage_card;
  let t2;
  if ($[1] !== ChangelogCard || $[2] !== has_cards || $[3] !== has_changelog_card || $[4] !== has_usage_card || $[5] !== is_usage_rendering || $[6] !== nav_collapsed || $[7] !== show_usage_card_first) {
    t2 = has_cards && !nav_collapsed && <CardsWrapper>{Number(has_changelog_card) + Number(has_usage_card) >= 2 && <CardBackgroundHint />}{show_usage_card_first ? <OutpostUsageCard /> : ChangelogCard ? ChangelogCard : is_usage_rendering && <OutpostUsageCard />}</CardsWrapper>;
    $[1] = ChangelogCard;
    $[2] = has_cards;
    $[3] = has_changelog_card;
    $[4] = has_usage_card;
    $[5] = is_usage_rendering;
    $[6] = nav_collapsed;
    $[7] = show_usage_card_first;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== nav_collapsed || $[10] !== status) {
    t3 = status && <StyledNavLinks>{nav_collapsed ? <Tooltip tooltip={[status_map[status?.status].text, status.version].filter(Boolean).join(" ")} side="right" align="center" offset={[0, 8]}><Div flex={{
          align: "center",
          justify: "center"
        }} p={{
          x: 1,
          y: 1
        }}><StyledStatusIndicator status={status?.status} /></Div></Tooltip> : <Div flex={{
        align: "center",
        justify: "space-between"
      }} p={{
        x: 1,
        y: 1
      }}><Div flex={{
          align: "center",
          gap: 2
        }}><StyledStatusIndicator status={status?.status} /><Text semi={true} size="m">{status_map[status?.status].text}</Text></Div>{status.version && <Badge neutral={true} subtle={true}>{status.version}</Badge>}</Div>}</StyledNavLinks>;
    $[9] = nav_collapsed;
    $[10] = status;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  let t5;
  if ($[12] !== nav_collapsed) {
    t4 = nav_collapsed ? <Tooltip tooltip="Settings" side="right" align="center" offset={[0, 8]}><Icon icon="settings" p={0.5} pointer={true} muted={true} /></Tooltip> : <Icon icon="settings" left={2} p={0.5} pointer={true} muted={true} />;
    t5 = !nav_collapsed && <Text semi={true} size="m">Settings</Text>;
    $[12] = nav_collapsed;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <StyledNavLinks><NavLink to="/settings">{t4}{t5}</NavLink></StyledNavLinks>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== nav_collapsed) {
    t7 = (opened, toggle) => <StyledNavLinks><ClickableArea w={nav_collapsed ? undefined : 100} flex={{
        align: "center",
        justify: nav_collapsed ? "center" : undefined
      }} highlight={opened} onClick={() => toggle(!opened)}>{nav_collapsed ? <Tooltip tooltip="Help" side="right" align="center" offset={[0, 8]}><Icon icon="help" pointer={true} p={0.5} /></Tooltip> : <Icon left={true} icon="help" pointer={true} p={0.5} />}{!nav_collapsed && <Text semi={true} size="m">Help</Text>}</ClickableArea></StyledNavLinks>;
    $[18] = nav_collapsed;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== history || $[21] !== setShowGetStarted || $[22] !== showFeed || $[23] !== show_get_started || $[24] !== unread_count) {
    t8 = toggle_0 => <><StyledCardSection p={2} flex={{
        gap: 0.5,
        direction: "column"
      }}>{!show_get_started && <ClickableArea p={1} flex={{
          align: "center"
        }} rounded={true} onClick={() => {
          history.push("/get-started");
          setShowGetStarted(true);
          toggle_0(false);
        }}><Icon pointer={true} icon="grid_view" left={true} muted={true} p={0.5} /><Text semi={true} size="m">Get Started</Text></ClickableArea>}<ClickableArea p={1} as="a" href={LINKS.outpost.docs} target="_blank" rel="noreferrer" flex={{
          align: "center"
        }} rounded={true}><Icon pointer={true} icon="docs" left={true} muted={true} p={0.5} /><Text semi={true} size="m">Documentation</Text></ClickableArea><ClickableArea p={1} as="a" href={LINKS.outpost.api_ref} target="_blank" rel="noreferrer" flex={{
          align: "center"
        }} rounded={true}><Icon pointer={true} icon="api" left={true} muted={true} p={0.5} /><Text semi={true} size="m">API Reference</Text></ClickableArea><ClickableArea flex={{
          align: "center"
        }} rounded={true} p={1} onClick={() => {
          showFeed();
          toggle_0(false);
        }}><Icon pointer={true} icon="new_releases" left={true} muted={true} p={0.5} /><Text semi={true} size="m" w={100} flex={{
            justify: "space-between",
            align: "center"
          }}>Changelog{unread_count > 0 && <Badge primary={true} subtle={true}>{unread_count}</Badge>}</Text></ClickableArea></StyledCardSection><StyledCardSection p={2} min_w={{
        px: 180
      }} flex={{
        gap: 0.5,
        direction: "column"
      }}><ClickableArea p={1} flex={{
          align: "center"
        }} rounded={true} onClick={showChat}><Icon pointer={true} left={true} icon="chat" muted={true} p={0.5} /><Text semi={true} size="m">Live Chat</Text></ClickableArea><ClickableArea p={1} flex={{
          align: "center"
        }} rounded={true} as="a" href="mailto:info@hookdeck.com" target="_blank" rel="noreferrer"><Icon pointer={true} icon="email" left={true} muted={true} p={0.5} /><Text semi={true} size="m">Email us</Text></ClickableArea><ClickableArea p={1} flex={{
          align: "center"
        }} rounded={true} as="a" href={LINKS.community} target="_blank" rel="noreferrer"><Icon pointer={true} icon="slack_colored" left={true} p={0.5} /><Text semi={true} size="m">Join Slack</Text></ClickableArea></StyledCardSection></>;
    $[20] = history;
    $[21] = setShowGetStarted;
    $[22] = showFeed;
    $[23] = show_get_started;
    $[24] = unread_count;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t7 || $[27] !== t8) {
    t9 = <Dropdown placement="right-end" block={true} p={0} renderToggle={t7}>{t8}</Dropdown>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      min: 225
    };
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== nav_collapsed || $[31] !== setNavCollapsed || $[32] !== user.name) {
    t11 = (opened_0, toggle_1) => <StyledNavLinks style={{
      flexDirection: nav_collapsed ? "column" : "row",
      alignItems: nav_collapsed ? "center" : "stretch"
    }}><ClickableArea highlight={opened_0} on="background" w={nav_collapsed ? {
        px: 28
      } : undefined} style={{
        flex: nav_collapsed ? "0 0 auto" : "1 1 auto",
        minWidth: 0
      }} flex={{
        align: "center",
        justify: nav_collapsed ? "center" : "space-between"
      }} onClick={() => toggle_1(!opened_0)}><Div w={nav_collapsed ? undefined : {
          px: 182
        }} flex={{
          align: "center",
          justify: nav_collapsed ? "center" : undefined
        }}>{nav_collapsed ? <Tooltip tooltip={capitalizeFirstLetter(user.name)} side="right" align="center" offset={[0, 8]}><Avatar name={user.name} small={true} neutral={true} /></Tooltip> : <Avatar name={user.name} small={true} neutral={true} m={{
            r: 2
          }} />}{!nav_collapsed && <Text semi={true} ellipsis={true}>{capitalizeFirstLetter(user.name)}</Text>}</Div></ClickableArea>{nav_collapsed ? <Tooltip tooltip="Expand sidebar" side="right" align="center" offset={[0, 8]}><ClickableArea aria-label="Expand sidebar" on="background" rounded={true} w={{
          px: 28
        }} h={{
          px: 28
        }} flex={{
          align: "center",
          justify: "center"
        }} p={1} onClick={() => setNavCollapsed(false)}><Icon muted={true} icon="left_panel_open" pointer={true} p={0.5} /></ClickableArea></Tooltip> : <Tooltip tooltip="Collapse sidebar" side="right" align="center" offset={[0, 8]}><ClickableArea aria-label="Collapse sidebar" on="background" rounded={true} w={{
          px: 28
        }} h={{
          px: 28
        }} flex={{
          align: "center",
          justify: "center"
        }} p={1} onClick={() => setNavCollapsed(true)}><Icon muted={true} icon="left_panel_close" pointer={true} p={0.5} /></ClickableArea></Tooltip>}</StyledNavLinks>;
    $[30] = nav_collapsed;
    $[31] = setNavCollapsed;
    $[32] = user.name;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== HookdeckAPI || $[35] !== theme || $[36] !== user.email || $[37] !== user.name) {
    t12 = toggle_2 => <><Div p={{
        x: 4,
        y: 3
      }}><Text ellipsis={true} semi={true}>{user.name}</Text><Text ellipsis={true} semi={true} muted={true}>{user.email}</Text></Div><Divider /><Div p={2}><Dropdown placement="right-start" portal={true} block={true} p={0} parent_width={{
          min: 225
        }} renderToggle={(opened_1, toggle_3) => <ClickableArea rounded={true} highlight={opened_1} flex={{
          align: "center",
          justify: "space-between"
        }} p={1} onClick={() => toggle_3(!opened_1)}><Div flex={{
            align: "center"
          }}><Icon p={0.5} pointer={true} muted={true} icon={theme.selected === "system" ? "computer" : theme.selected === "light" ? "light_mode" : "dark_mode"} left={true} /><Text semi={true} dark={true}>Theme</Text></Div><Icon icon="chevron_right" left={1} pointer={true} /></ClickableArea>}>{toggle_4 => <Div p={{
            x: 2,
            y: 2.5
          }}><ClickableArea p={1} flex={{
              align: "center",
              justify: "space-between"
            }} rounded={true} onClick={() => {
              toggle_4(false);
              theme.toggleTheme("light");
            }}><Div flex={{
                align: "center"
              }}><Icon icon="light_mode" left={true} muted={true} pointer={true} p={0.5} /><Text semi={true} size="m">Light</Text></Div>{theme.selected === "light" && <Icon icon="success" pointer={true} primary={true} />}</ClickableArea><ClickableArea p={1} flex={{
              align: "center",
              justify: "space-between"
            }} rounded={true} onClick={() => {
              toggle_4(false);
              theme.toggleTheme("dark");
            }}><Div flex={{
                align: "center"
              }}><Icon icon="dark_mode" left={true} muted={true} pointer={true} p={0.5} /><Text semi={true} size="m">Dark</Text></Div>{theme.selected === "dark" && <Icon icon="success" pointer={true} primary={true} />}</ClickableArea><ClickableArea p={1} flex={{
              align: "center",
              justify: "space-between"
            }} rounded={true} onClick={() => {
              toggle_4(false);
              theme.toggleTheme("system");
            }}><Div flex={{
                align: "center"
              }}><Icon icon="computer" left={true} muted={true} pointer={true} p={0.5} /><Text semi={true} size="m">System</Text></Div>{theme.selected === "system" && <Icon icon="success" pointer={true} primary={true} />}</ClickableArea></Div>}</Dropdown></Div><Divider /><Div p={2}><ClickableArea onClick={() => {
          toggle_2(false);
          handleLogout(HookdeckAPI);
        }} p={1} rounded={true} flex={{
          align: "center"
        }}><Icon icon="logout" left={2} p={0.5} pointer={true} muted={true} /><Text semi={true}>Sign out</Text></ClickableArea></Div></>;
    $[34] = HookdeckAPI;
    $[35] = theme;
    $[36] = user.email;
    $[37] = user.name;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t11 || $[40] !== t12) {
    t13 = <Dropdown placement="right-end" parent_width={t10} renderToggle={t11}>{t12}</Dropdown>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t13 || $[43] !== t3 || $[44] !== t6 || $[45] !== t9) {
    t14 = <StyledNavLinks>{t3}{t6}{t9}{t13}</StyledNavLinks>;
    $[42] = t13;
    $[43] = t3;
    $[44] = t6;
    $[45] = t9;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== ChangelogFeed || $[48] !== nav_collapsed || $[49] !== t14 || $[50] !== t2) {
    t15 = <StyledNavFooter collapsed={nav_collapsed}>{t2}{t14}{ChangelogFeed}</StyledNavFooter>;
    $[47] = ChangelogFeed;
    $[48] = nav_collapsed;
    $[49] = t14;
    $[50] = t2;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
};
export default OutpostNavFooter;
function _temp(entry) {
  return entry.billable_metric.name.toLowerCase() === "outpost events";
}
function _temp2(price) {
  const price_name = price.name.toLowerCase();
  return price_name === "outpost events";
}
function _temp3(v) {
  return v;
}
function _temp4(v_0) {
  return v_0;
}
function _temp5(location) {
  return {
    ...location,
    pathname: "/settings/organization/billing/payment-method",
    state: {
      scroll: false
    }
  };
}